// ============================================================================
// LEARNING GLYPHS — transcribed path-for-path from LifeOS Learning.dc.html (9a/9b).
//
// A third icon module, alongside components/icons.tsx and components/school/icons.tsx, for the
// same boring reason school's exists: parallel screen ports editing one shared file merge badly.
// Anything a second screen needs moves up to components/icons.tsx on its second use (the Rule of
// Three, blueprint/02).
//
// Same contract as the other two: viewBox 0 0 24 24, fill none, stroke="currentColor" so the
// colour comes from a token class, round caps and joins. A raw hex in here would fail the ship
// gate's brand audit, which is the point.
// ============================================================================

import type { IconProps } from '../icons'

function Glyph({ size = 16, strokeWidth = 1.7, className, d }: IconProps & { d: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      <path d={d} />
    </svg>
  )
}

/** The streak flame, in the header band and on a track card. */
export const Flame = (p: IconProps) => (
  <Glyph
    strokeWidth={1.9}
    {...p}
    d="M12 20.4c3.4 0 5.6-2.3 5.6-5.4 0-3.8-3.4-5.2-2.6-9.4-2.6.6-4.3 2.6-4.3 4.6 0 1.4-.8 2-1.6 2-1 0-1.6-.9-1.6-2.2-1.4 1.4-2.1 3.2-2.1 5 0 3.1 2.2 5.4 6.6 5.4z"
  />
)

/** Ahead of the plan. */
export const ArrowUp = (p: IconProps) => <Glyph strokeWidth={2.6} {...p} d="M12 19V5M6 11l6-6 6 6" />

/** Behind the plan. The design uses direction, not just colour, to say which way it went. */
export const ArrowDown = (p: IconProps) => (
  <Glyph strokeWidth={2.6} {...p} d="M12 5v14M6 13l6 6 6-6" />
)

/** 'Open track →' */
export const ArrowRight = (p: IconProps) => (
  <Glyph strokeWidth={2.2} {...p} d="M5 12h13M12.6 6l6 6-6 6" />
)

export const ChevronDown = (p: IconProps) => <Glyph strokeWidth={2} {...p} d="M6 9.5l6 6 6-6" />

export const ChevronLeft = (p: IconProps) => <Glyph strokeWidth={2} {...p} d="M14 6l-6 6 6 6" />

/** The plan-provenance file. */
export const PlanDoc = (p: IconProps) => (
  <Glyph strokeWidth={1.8} {...p} d="M6 3.6h8.4L19 8.2v12.2H6zM14 3.6v5h5" />
)

/** 'Reschedule remaining' — the plan re-flows, it is not thrown away. */
export const Reschedule = (p: IconProps) => (
  <Glyph
    strokeWidth={1.9}
    {...p}
    d="M19.4 10.6a7.6 7.6 0 0 0-13-3.4L4.6 9M4.6 13.4a7.6 7.6 0 0 0 13 3.4L19.4 15M4.6 5.4v3.8h3.8M19.4 18.6v-3.8h-3.8"
  />
)

/** A finished unit, and a finished item inside one. */
export const Tick = (p: IconProps) => <Glyph strokeWidth={2.4} {...p} d="M5 12.6l4 4L19 7.2" />

/**
 * OPENS IN A NEW TAB — the box with the arrow leaving it. On every resource control, because a
 * link that takes the page away from a checklist mid-session would be the app costing him the
 * thing it is for.
 */
export const ExternalLink = (p: IconProps) => (
  <Glyph strokeWidth={2} {...p} d="M14 4.6h5.4V10M19.4 4.6l-7.6 7.6M16.4 14v4.6a.8.8 0 0 1-.8.8H5.4a.8.8 0 0 1-.8-.8V8.4a.8.8 0 0 1 .8-.8H10" />
)

/** The Resources section: a shelf. */
export const Shelf = (p: IconProps) => (
  <Glyph strokeWidth={1.8} {...p} d="M4.6 5.2h4v13.6h-4zM10.4 5.2h4v13.6h-4zM16.6 6.4l3.2.8-3 12.4-3.1-.8z" />
)
